<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- 標題和操作區域 -->
        <div class="d-flex flex-wrap flex-lg-nowrap align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <h3 class="font-weight-bolder text-nowrap mb-0">
              <feather-icon size="18" icon="FileTextIcon" />
              破產令記錄
            </h3>
            <div class="form-col-select ml-2">
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
              />
              <feather-icon size="18" icon="ChevronDownIcon" />
            </div>
          </div>

          <div class="d-flex align-items-center">
            <!-- 搜尋框 -->
            <div class="search-primary mr-2">
              <feather-icon size="16" icon="SearchIcon" />
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="搜尋姓名、英文姓名、身份證..."
              />
            </div>

            <!-- 年份篩選 -->
            <v-select
              v-model="selectedYear"
              :options="yearOptions"
              :clearable="true"
              placeholder="選擇年份"
              class="year-selector mr-2"
            />

            <!-- 新增記錄按鈕 -->
            <b-button
              variant="success"
              @click="addRecord"
              class="mr-2"
            >
              <feather-icon size="16" icon="PlusIcon" class="mr-1" />
              新增記錄
            </b-button>

            <!-- 上傳按鈕 -->
            <b-button
              variant="primary"
              @click="$router.push('/bankruptcy/upload')"
            >
              <feather-icon size="16" icon="UploadIcon" class="mr-1" />
              上傳PDF
            </b-button>
          </div>
        </div>

        <!-- 工具列 -->
        <div class="d-flex align-items-center justify-content-between mt-3">
          <div class="d-flex align-items-center">
            <!-- 過濾無英文名記錄 -->
            <b-form-checkbox
              v-model="filterNoEnglishName"
              switch
              size="sm"
              class="mr-3"
            >
              <small class="font-weight-bold">僅顯示無英文名記錄</small>
            </b-form-checkbox>

            <!-- 過濾中英文名稱字數不一致記錄 -->
            <b-form-checkbox
              v-model="filterNameLengthMismatch"
              switch
              size="sm"
              class="mr-3"
            >
              <small class="font-weight-bold">僅顯示中英文名稱字數不一致（未確認）</small>
            </b-form-checkbox>

            <!-- 過濾拼音單詞長度異常記錄 -->
            <b-form-checkbox
              v-model="filterPinyinWordMismatch"
              switch
              size="sm"
              class="mr-3"
              v-b-tooltip.hover
              title="【檢測原理】2字中文名通常對應2個短拼音（姓+名）。找出第2單詞>=6字母者，可能是多音節連寫（如：李悅→Li Yuexin，Yuexin疑為Yue+Xin）。⚠️【可能誤判】第2單詞可能是真實英文名或單音節拼音（如：陳明→Chan Robert；張莊→Zhang Chuang），需人工確認。"
            >
              <small class="font-weight-bold">僅顯示拼音單詞過長（未確認）</small>
            </b-form-checkbox>
          </div>

          <div class="d-flex align-items-center">
            <small class="text-muted mr-2">
              總計 {{ totalRecords }} 筆記錄
            </small>
            <!-- 批量保存按鈕 -->
            <b-button
              v-if="hasChanges"
              variant="success"
              size="sm"
              @click="batchSaveChecked"
              :disabled="saveLoading"
              class="mr-2"
            >
              <feather-icon size="14" icon="SaveIcon" class="mr-1" />
              儲存變更 ({{ Object.keys(changedRecords).length }})
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="refreshData"
              :disabled="loading"
            >
              <feather-icon size="14" icon="RefreshCwIcon" class="mr-1" />
              重新整理
            </b-button>
          </div>
        </div>

        <!-- 未儲存變更提示 -->
        <b-alert
          v-if="hasChanges"
          show
          variant="warning"
          class="mt-3 mb-0 d-flex align-items-center justify-content-between"
        >
          <div class="d-flex align-items-center">
            <feather-icon size="18" icon="AlertCircleIcon" class="mr-2" />
            <span>
              <strong>尚有 {{ Object.keys(changedRecords).length }} 筆記錄未儲存</strong>
              - 請點擊「儲存變更」按鈕以保存修改
            </span>
          </div>
          <b-button
            variant="success"
            size="sm"
            @click="batchSaveChecked"
            :disabled="saveLoading"
          >
            <feather-icon size="14" icon="SaveIcon" class="mr-1" />
            立即儲存
          </b-button>
        </b-alert>
      </div>

      <!-- 資料表格 -->
      <b-table
        ref="bankruptcyTable"
        :items="fetchRecords"
        :fields="tableColumns"
        :busy="loading"
        responsive
        striped
        hover
        bordered
        small
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        empty-text="未找到匹配的記錄"
        table-class="bankruptcy-table"
      >
        <!-- 載入狀態 -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-2" />
            <strong>載入中...</strong>
          </div>
        </template>

        <!-- 姓名欄位 -->
        <template #cell(name)="data">
          <div class="d-flex align-items-center">
            <strong>{{ data.item.name }}</strong>
          </div>
        </template>

        <!-- 英文姓名欄位 -->
        <template #cell(name_en)="data">
          <div class="d-flex align-items-center">
            <span v-if="data.item.name_en">{{ data.item.name_en }}</span>
            <span class="text-muted small" v-else>-</span>
          </div>
        </template>

        <!-- 確認狀態欄位 -->
        <template #cell(is_checked)="data">
          <div class="d-flex align-items-center justify-content-center">
            <b-form-checkbox
              :checked="getCheckedStatus(data.item) === 1"
              @change="toggleCheckedLocal(data.item)"
              switch
              size="sm"
              :value="true"
              :unchecked-value="false"
            />
          </div>
        </template>

        <!-- 身份證前綴欄位 -->
        <template #cell(id_prefix)="data">
          <b-badge variant="secondary">{{ data.item.id_prefix }}</b-badge>
        </template>

        <!-- 年份欄位 -->
        <template #cell(year)="data">
          <b-badge variant="primary">{{ data.item.year }}年</b-badge>
        </template>

        <!-- 案件編號欄位 -->
        <template #cell(case_number)="data">
          <span class="text-nowrap">第{{ data.item.case_number }}宗</span>
        </template>

        <!-- 公告類型欄位 -->
        <template #cell(notice_type)="data">
          <b-badge
            :variant="getNoticeTypeVariant(data.item.notice_type)"
            class="text-nowrap"
          >
            {{ data.item.notice_type }}
          </b-badge>
        </template>

        <!-- 來源檔案欄位 -->
        <template #cell(source_file)="data">
          <span v-if="data.item.source_file" class="text-muted small">
            {{ data.item.source_file.replace('bankruptcy_', '').substring(0, 20) }}...
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- 建立時間欄位 -->
        <template #cell(created_at)="data">
          <div class="text-nowrap">
            <small class="text-muted">{{ formatDateTime(data.item.created_at) }}</small>
          </div>
        </template>

        <!-- 操作欄位 -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <b-button
              variant="link"
              size="sm"
              class="text-primary p-0 mr-2"
              @click="viewDetails(data.item)"
              v-b-tooltip.hover
              title="查看詳情"
            >
              <feather-icon size="16" icon="EyeIcon" />
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="text-warning p-0 mr-2"
              @click="editRecord(data.item)"
              v-b-tooltip.hover
              title="編輯"
            >
              <feather-icon size="16" icon="EditIcon" />
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="text-danger p-0"
              @click="deleteRecord(data.item)"
              v-b-tooltip.hover
              title="刪除"
            >
              <feather-icon size="16" icon="TrashIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- 分頁控制 -->
      <div v-if="totalRecords > (perPage && perPage.value || 15)" class="mx-2 mb-2">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage && perPage.value || 15"
          first-number
          last-number
          class="mb-0 mt-1 justify-content-center"
        />
      </div>
    </b-card>

    <!-- 詳情模態框 -->
    <b-modal
      id="modal-record-details"
      title="破產令記錄詳情"
      size="lg"
      ok-only
      ok-title="關閉"
    >
      <div v-if="selectedRecord">
        <b-row class="mb-2">
          <b-col sm="3"><strong>姓名:</strong></b-col>
          <b-col sm="9">{{ selectedRecord.name }}</b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>英文姓名:</strong></b-col>
          <b-col sm="9">
            <span v-if="selectedRecord.name_en">{{ selectedRecord.name_en }}</span>
            <span v-else class="text-muted">未匹配</span>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>身份證前綴:</strong></b-col>
          <b-col sm="9">
            <b-badge variant="secondary">{{ selectedRecord.id_prefix }}</b-badge>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>年份:</strong></b-col>
          <b-col sm="9">{{ selectedRecord.year }}年</b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>案件編號:</strong></b-col>
          <b-col sm="9">第{{ selectedRecord.case_number }}宗</b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>公告類型:</strong></b-col>
          <b-col sm="9">
            <b-badge :variant="getNoticeTypeVariant(selectedRecord.notice_type)">
              {{ selectedRecord.notice_type }}
            </b-badge>
          </b-col>
        </b-row>
        <b-row class="mb-2" v-if="selectedRecord.source_file">
          <b-col sm="3"><strong>來源檔案:</strong></b-col>
          <b-col sm="9">{{ selectedRecord.source_file }}</b-col>
        </b-row>
        <b-row class="mb-2" v-if="selectedRecord.page_number">
          <b-col sm="3"><strong>頁碼:</strong></b-col>
          <b-col sm="9">第{{ selectedRecord.page_number }}頁</b-col>
        </b-row>
        <b-row class="mb-2" v-if="selectedRecord.original_text">
          <b-col sm="3"><strong>原始文字:</strong></b-col>
          <b-col sm="9">
            <small class="text-muted">{{ selectedRecord.original_text }}</small>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>建立時間:</strong></b-col>
          <b-col sm="9">{{ formatDateTime(selectedRecord.created_at) }}</b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="3"><strong>更新時間:</strong></b-col>
          <b-col sm="9">{{ formatDateTime(selectedRecord.updated_at) }}</b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- 編輯模態框 -->
    <b-modal
      id="modal-record-edit"
      title="編輯破產令記錄"
      size="md"
      @ok="saveRecord"
      ok-title="保存"
      cancel-title="取消"
      centered
      :hide-header-close="saveLoading"
      :no-close-on-backdrop="saveLoading"
      :no-close-on-esc="saveLoading"
    >
      <div v-if="editedRecord">
        <b-form>
          <b-row class="mb-3">
            <b-col sm="4"><strong class="text-danger">* 中文姓名:</strong></b-col>
            <b-col sm="8">
              <b-form-input
                v-model="editedRecord.name"
                :disabled="saveLoading"
                required
                placeholder="請輸入中文姓名"
              />
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col sm="4"><strong>英文姓名:</strong></b-col>
            <b-col sm="8">
              <b-form-input v-model="editedRecord.name_en" placeholder="請輸入英文姓名（可選）" :disabled="saveLoading" />
            </b-col>
          </b-row>
        </b-form>
      </div>

      <!-- Loading Overlay -->
      <div v-if="saveLoading" class="position-absolute d-flex align-items-center justify-content-center"
           style="top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.9); z-index: 1000;">
        <div class="text-center">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
          <div class="mt-3 text-primary font-weight-bold">保存中...</div>
        </div>
      </div>
    </b-modal>

    <!-- 新增記錄模態框 -->
    <b-modal
      id="modal-record-add"
      title="新增破產令記錄"
      size="md"
      @ok="saveNewRecord"
      ok-title="儲存"
      cancel-title="取消"
      centered
      :hide-header-close="saveLoading"
      :no-close-on-backdrop="saveLoading"
      :no-close-on-esc="saveLoading"
    >
      <b-form>
        <b-row class="mb-3">
          <b-col sm="4"><strong class="text-danger">* 中文姓名:</strong></b-col>
          <b-col sm="8">
            <b-form-input
              v-model="newRecord.name"
              :disabled="saveLoading"
              required
              placeholder="請輸入中文姓名"
            />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col sm="4"><strong>英文姓名:</strong></b-col>
          <b-col sm="8">
            <b-form-input
              v-model="newRecord.name_en"
              :disabled="saveLoading"
              placeholder="請輸入英文姓名（可選）"
            />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col sm="4"><strong>身份證前綴:</strong></b-col>
          <b-col sm="8">
            <b-form-input
              v-model="newRecord.id_prefix"
              :disabled="saveLoading"
              maxlength="4"
              placeholder="例如：A123"
            />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col sm="4"><strong class="text-danger">* 年份:</strong></b-col>
          <b-col sm="8">
            <b-form-input
              v-model="newRecord.year"
              :disabled="saveLoading"
              type="number"
              min="1950"
              max="2050"
              required
              placeholder="請輸入年份"
            />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col sm="4"><strong class="text-danger">* 案件編號:</strong></b-col>
          <b-col sm="8">
            <b-form-input
              v-model="newRecord.case_number"
              :disabled="saveLoading"
              required
              placeholder="請輸入案件編號"
            />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col sm="4"><strong class="text-danger">* 公告類型:</strong></b-col>
          <b-col sm="8">
            <b-form-select
              v-model="newRecord.notice_type"
              :disabled="saveLoading"
              :options="noticeTypeOptions"
              required
            >
              <template #first>
                <b-form-select-option :value="null" disabled>-- 請選擇公告類型 --</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
        </b-row>
      </b-form>

      <!-- Loading Overlay -->
      <div v-if="saveLoading" class="position-absolute d-flex align-items-center justify-content-center"
           style="top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.9); z-index: 1000;">
        <div class="text-center">
          <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
          <div class="mt-3 text-primary font-weight-bold">保存中...</div>
        </div>
      </div>
    </b-modal>

    <!-- 刪除 Loading Overlay -->
    <b-overlay
      :show="deleteLoading"
      variant="transparent"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      no-wrap
    >
      <template #overlay>
        <div class="d-flex flex-column align-center justify-center" style="min-height: 200px;">
          <b-spinner
            variant="danger"
            style="width: 4rem; height: 4rem; border-width: 3px;"
          />
          <div class="mt-4 text-h6 text-white font-weight-bold">
            刪除中...
          </div>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from '@vue/composition-api'
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormSelect,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BPagination,
  BSpinner,
  BFormCheckbox,
  BModal,
  BForm,
  BAlert,
  BOverlay,
  VBTooltip
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BSpinner,
    BFormCheckbox,
    BModal,
    BForm,
    BAlert,
    BOverlay,
    vSelect
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  setup(props, { root }) {
    // 響應式數據
    const loading = ref(false)
    const bankruptcyTable = ref(null)
    const records = ref([])
    const totalRecords = ref(0)
    const currentPage = ref(1)
    const perPage = ref({ label: '15', value: 15 })
    const sortBy = ref('created_at')
    const isSortDirDesc = ref(true)
    const searchQuery = ref('')
    const selectedYear = ref(null)
    const selectedRecord = ref(null)
    const filterNoEnglishName = ref(false)
    const filterNameLengthMismatch = ref(false)
    const filterPinyinWordMismatch = ref(false)
    const editedRecord = ref(null)
    const newRecord = ref({
      name: '',
      name_en: '',
      id_prefix: '',
      year: new Date().getFullYear(),
      case_number: '',
      notice_type: null
    })
    const saveLoading = ref(false)
    const deleteLoading = ref(false)
    const changedRecords = ref({}) // 儲存變更的記錄 {id: new_status}

    // 配置選項
    const perPageOptions = [
      { label: '10', value: 10 },
      { label: '15', value: 15 },
      { label: '25', value: 25 },
      { label: '50', value: 50 },
      { label: '100', value: 100 }
    ]
    const yearOptions = computed(() => {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 1950; year--) {
        years.push({ label: `${year}年`, value: year })
      }
      return years
    })
    const noticeTypeOptions = [
      { value: '頒布破產令公告', text: '頒布破產令公告' },
      { value: '破產令公告', text: '破產令公告' },
      { value: '債權人會議', text: '債權人會議' },
      { value: '分派股息', text: '分派股息' },
      { value: '委任受託人', text: '委任受託人' }
    ]

    // 表格欄位
    const tableColumns = [
      {
        key: 'name',
        label: '姓名',
        sortable: true,
        thStyle: { minWidth: '120px' },
        tdClass: 'align-middle'
      },
      {
        key: 'name_en',
        label: '英文姓名',
        sortable: true,
        thStyle: { minWidth: '150px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'is_checked',
        label: '已確認',
        sortable: true,
        class: 'text-center',
        thClass: 'text-center',
        thStyle: { width: '90px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'id_prefix',
        label: '身份證前綴',
        sortable: true,
        class: 'text-center',
        thClass: 'text-center',
        thStyle: { width: '110px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'year',
        label: '年份',
        sortable: true,
        class: 'text-center',
        thClass: 'text-center',
        thStyle: { width: '80px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'case_number',
        label: '案件編號',
        sortable: true,
        class: 'text-center',
        thClass: 'text-center',
        thStyle: { width: '100px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'notice_type',
        label: '公告類型',
        sortable: true,
        thStyle: { minWidth: '120px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'source_file',
        label: '來源檔案',
        sortable: false,
        thStyle: { width: '150px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'created_at',
        label: '建立時間',
        sortable: true,
        thStyle: { width: '130px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      },
      {
        key: 'actions',
        label: '操作',
        sortable: false,
        class: 'text-center',
        thClass: 'text-center',
        thStyle: { width: '80px', verticalAlign: 'middle' },
        tdClass: 'align-middle'
      }
    ]

    // 監聽搜尋和篩選變化（防抖動）
    let searchTimeout = null
    watch([searchQuery, selectedYear, filterNoEnglishName, filterNameLengthMismatch, filterPinyinWordMismatch], () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        currentPage.value = 1
        root.$nextTick(() => {
          if (bankruptcyTable.value) {
            bankruptcyTable.value.refresh()
          }
        })
      }, 500)
    })

    // perPage 變化立即觸發（不需要防抖動）
    watch(perPage, () => {
      currentPage.value = 1
      root.$nextTick(() => {
        if (bankruptcyTable.value) {
          bankruptcyTable.value.refresh()
        }
      })
    })

    // 獲取記錄數據
    const fetchRecords = async (ctx) => {
      loading.value = true
      try {
        const token = localStorage.getItem('accessToken')
        const params = {
          q: searchQuery.value || null,
          perPage: perPage.value && perPage.value.value || 15,
          page: currentPage.value,
          sortBy: ctx && ctx.sortBy || sortBy.value,
          sortDesc: ctx && ctx.sortDesc !== undefined ? ctx.sortDesc : isSortDirDesc.value,
          year: selectedYear.value ? selectedYear.value.value : null,
          has_chinese_no_english: filterNoEnglishName.value || null,
          name_length_mismatch: filterNameLengthMismatch.value || null,
          pinyin_word_length_mismatch: filterPinyinWordMismatch.value || null
        }

        const response = await axios.get(
          `${process.env.VUE_APP_SERVICE_URL}/bankruptcy/list`,
          {
            params,
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.success) {
          records.value = response.data.data || []
          totalRecords.value = response.data.total || 0
          return response.data.data || []
        } else {
          throw new Error(response.data.message)
        }

      } catch (error) {
        console.error('Fetch error:', error)
        root.$bvToast.toast(
          error.response && error.response.data && error.response.data.message || '載入數據失敗',
          {
            title: '錯誤',
            variant: 'danger',
            solid: true
          }
        )
        return []
      } finally {
        loading.value = false
      }
    }

    // 刷新數據
    const refreshData = () => {
      root.$nextTick(() => {
        if (bankruptcyTable.value) {
          bankruptcyTable.value.refresh()
        }
      })
    }

    // 查看詳情
    const viewDetails = (record) => {
      selectedRecord.value = record
      root.$bvModal.show('modal-record-details')
    }

    // 編輯記錄
    const editRecord = (record) => {
      editedRecord.value = {
        id: record.id,
        name: record.name,
        name_en: record.name_en || ''
      }
      root.$bvModal.show('modal-record-edit')
    }

    // 保存記錄
    const saveRecord = async (event) => {
      event.preventDefault()

      // 前端驗證：姓名不可為空
      if (!editedRecord.value.name || editedRecord.value.name.trim() === '') {
        root.$bvToast.toast('請輸入姓名', {
          title: '驗證錯誤',
          variant: 'danger',
          solid: true
        })
        return
      }

      saveLoading.value = true

      try {
        const token = localStorage.getItem('accessToken')
        // 只發送 name 和 name_en
        const updateData = {
          name: editedRecord.value.name.trim(),
          name_en: editedRecord.value.name_en ? editedRecord.value.name_en.trim() : ''
        }
        const response = await axios.put(
          `${process.env.VUE_APP_SERVICE_URL}/bankruptcy/${editedRecord.value.id}`,
          updateData,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.success) {
          root.$bvToast.toast('記錄已更新', {
            title: '更新成功',
            variant: 'success',
            solid: true
          })
          root.$bvModal.hide('modal-record-edit')
          refreshData()
        } else {
          throw new Error(response.data.message)
        }

      } catch (error) {
        console.error('Update error:', error)
        root.$bvToast.toast(
          error.response && error.response.data && error.response.data.message || '更新失敗',
          {
            title: '錯誤',
            variant: 'danger',
            solid: true
          }
        )
      } finally {
        saveLoading.value = false
      }
    }

    // 打開新增記錄模態框
    const addRecord = () => {
      // 重置表單
      newRecord.value = {
        name: '',
        name_en: '',
        id_prefix: '',
        year: new Date().getFullYear(),
        case_number: '',
        notice_type: null
      }
      root.$bvModal.show('modal-record-add')
    }

    // 保存新記錄
    const saveNewRecord = async (event) => {
      event.preventDefault()

      // 前端驗證：必填欄位
      if (!newRecord.value.name || newRecord.value.name.trim() === '') {
        root.$bvToast.toast('請輸入中文姓名', {
          title: '驗證錯誤',
          variant: 'danger',
          solid: true
        })
        return
      }

      if (!newRecord.value.year) {
        root.$bvToast.toast('請輸入年份', {
          title: '驗證錯誤',
          variant: 'danger',
          solid: true
        })
        return
      }

      if (!newRecord.value.case_number || newRecord.value.case_number.trim() === '') {
        root.$bvToast.toast('請輸入案件編號', {
          title: '驗證錯誤',
          variant: 'danger',
          solid: true
        })
        return
      }

      if (!newRecord.value.notice_type) {
        root.$bvToast.toast('請選擇公告類型', {
          title: '驗證錯誤',
          variant: 'danger',
          solid: true
        })
        return
      }

      saveLoading.value = true

      try {
        const token = localStorage.getItem('accessToken')
        const createData = {
          name: newRecord.value.name.trim(),
          name_en: newRecord.value.name_en ? newRecord.value.name_en.trim() : '',
          id_prefix: newRecord.value.id_prefix ? newRecord.value.id_prefix.trim() : '',
          year: parseInt(newRecord.value.year),
          case_number: newRecord.value.case_number.trim(),
          notice_type: newRecord.value.notice_type
        }

        const response = await axios.post(
          `${process.env.VUE_APP_SERVICE_URL}/bankruptcy/`,
          createData,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.success) {
          root.$bvToast.toast(`破產令記錄「${createData.name}」已成功創建`, {
            title: '創建成功',
            variant: 'success',
            solid: true
          })
          root.$bvModal.hide('modal-record-add')
          refreshData()
        } else {
          // 處理錯誤（包括重複記錄）
          let errorMessage = response.data.message || '創建失敗'
          if (response.data.errors) {
            const errors = Object.values(response.data.errors).flat()
            errorMessage = errors.join(', ')
          }
          root.$bvToast.toast(errorMessage, {
            title: '創建失敗',
            variant: 'danger',
            solid: true
          })
        }

      } catch (error) {
        console.error('Create error:', error)
        let errorMessage = '創建失敗，請重試'
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || errorMessage
          if (error.response.data.errors) {
            const errors = Object.values(error.response.data.errors).flat()
            errorMessage = errors.join(', ')
          }
        }
        root.$bvToast.toast(errorMessage, {
          title: '錯誤',
          variant: 'danger',
          solid: true
        })
      } finally {
        saveLoading.value = false
      }
    }

    // 刪除記錄
    const deleteRecord = async (record) => {
      // 顯示確認對話框
      const confirmed = await root.$bvModal.msgBoxConfirm(
        `確定要刪除破產令記錄嗎？\n\n姓名：${record.name}\n年份：${record.year}年\n案件編號：第${record.case_number}宗\n\n此操作無法復原。`,
        {
          title: '確認刪除',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: '確定刪除',
          cancelTitle: '取消',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (!confirmed) {
        return
      }

      deleteLoading.value = true

      try {
        const token = localStorage.getItem('accessToken')
        const response = await axios.delete(
          `${process.env.VUE_APP_SERVICE_URL}/bankruptcy/`,
          {
            params: { id: record.id },
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (response.data.success) {
          root.$bvToast.toast(`破產令記錄「${record.name}」已成功刪除`, {
            title: '刪除成功',
            variant: 'success',
            solid: true
          })
          refreshData()
        } else {
          throw new Error(response.data.message)
        }

      } catch (error) {
        console.error('Delete error:', error)
        root.$bvToast.toast(
          error.response && error.response.data && error.response.data.message || '刪除失敗，請重試',
          {
            title: '錯誤',
            variant: 'danger',
            solid: true
          }
        )
      } finally {
        deleteLoading.value = false
      }
    }

    // 本地切換確認狀態（不立即保存）
    const toggleCheckedLocal = (record) => {
      console.log('Toggle clicked for record:', record.id)
      const currentStatus = getCheckedStatus(record)
      const newStatus = currentStatus === 1 ? 0 : 1
      console.log('Current status:', currentStatus, 'New status:', newStatus)

      // 如果新狀態等於原始狀態，從變更記錄中移除
      if (newStatus === record.is_checked) {
        console.log('Status returned to original, removing from changes')
        root.$delete(changedRecords.value, record.id)
      } else {
        // 使用 Vue.set 確保響應式更新
        root.$set(changedRecords.value, record.id, newStatus)
      }
      console.log('Changed records:', changedRecords.value)
    }

    // 獲取記錄的確認狀態（優先從變更記錄中取得）
    const getCheckedStatus = (record) => {
      if (changedRecords.value.hasOwnProperty(record.id)) {
        return changedRecords.value[record.id]
      }
      return record.is_checked
    }

    // 批量保存確認狀態
    const batchSaveChecked = async () => {
      const changeCount = Object.keys(changedRecords.value).length
      if (changeCount === 0) return

      saveLoading.value = true

      try {
        const token = localStorage.getItem('accessToken')
        const updates = []

        // 準備所有更新請求
        for (const [id, is_checked] of Object.entries(changedRecords.value)) {
          updates.push(
            axios.patch(
              `${process.env.VUE_APP_SERVICE_URL}/bankruptcy/${id}/check`,
              { is_checked },
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            )
          )
        }

        // 批量執行所有請求
        const results = await Promise.all(updates)

        // 檢查是否全部成功
        const allSuccess = results.every(res => res.data.success)

        if (allSuccess) {
          root.$bvToast.toast(
            `成功更新 ${changeCount} 筆記錄`,
            {
              title: '批量更新成功',
              variant: 'success',
              solid: true
            }
          )
          // 清空變更記錄
          changedRecords.value = {}
          // 刷新資料
          refreshData()
        } else {
          throw new Error('部分記錄更新失敗')
        }

      } catch (error) {
        console.error('Batch save error:', error)
        root.$bvToast.toast(
          error.response && error.response.data && error.response.data.message || '批量更新失敗',
          {
            title: '錯誤',
            variant: 'danger',
            solid: true
          }
        )
      } finally {
        saveLoading.value = false
      }
    }

    // 計算屬性：是否有變更
    const hasChanges = computed(() => Object.keys(changedRecords.value).length > 0)


    // 工具函數
    const formatDateTime = (datetime) => {
      if (!datetime) return '-'
      const date = new Date(datetime)
      return date.toLocaleString('zh-TW')
    }

    const getNoticeTypeVariant = (type) => {
      const variants = {
        '頒布破產令公告': 'danger',
        '破產令公告': 'warning',
        '債權人會議': 'info',
        '分派股息': 'success',
        '委任受託人': 'secondary'
      }
      return variants[type] || 'light'
    }

    // 監聽分頁和排序變化
    watch([currentPage, sortBy, isSortDirDesc], () => {
      root.$nextTick(() => {
        if (bankruptcyTable.value) {
          bankruptcyTable.value.refresh()
        }
      })
    })

    return {
      // 數據
      loading,
      bankruptcyTable,
      records,
      totalRecords,
      currentPage,
      perPage,
      sortBy,
      isSortDirDesc,
      searchQuery,
      selectedYear,
      selectedRecord,
      filterNoEnglishName,
      filterNameLengthMismatch,
      filterPinyinWordMismatch,
      editedRecord,
      newRecord,
      saveLoading,
      deleteLoading,
      changedRecords,
      hasChanges,
      // 配置
      perPageOptions,
      yearOptions,
      tableColumns,
      noticeTypeOptions,
      // 方法
      fetchRecords,
      refreshData,
      viewDetails,
      editRecord,
      saveRecord,
      addRecord,
      saveNewRecord,
      deleteRecord,
      toggleCheckedLocal,
      getCheckedStatus,
      batchSaveChecked,
      formatDateTime,
      getNoticeTypeVariant
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.per-page-selector {
  width: 90px;
}

.search-primary {
  position: relative;
  max-width: 300px;
}

.search-primary .feather-icon {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}

.search-primary input {
  padding-left: 35px;
}

.year-selector {
  min-width: 120px;
}

/* 表格樣式改進 */
::v-deep .bankruptcy-table {
  margin-bottom: 0;
}

::v-deep .bankruptcy-table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  padding: 12px 8px;
}

::v-deep .bankruptcy-table tbody td {
  padding: 10px 8px;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep .bankruptcy-table tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

/* 特定欄位樣式 */
::v-deep .bankruptcy-table tbody td .badge {
  font-size: 0.75rem;
  padding: 4px 8px;
}

::v-deep .bankruptcy-table tbody td .dropdown-toggle::after {
  display: none;
}

/* 響應式改進 */
@media (max-width: 1200px) {
  ::v-deep .bankruptcy-table tbody td {
    padding: 8px 6px;
    font-size: 0.8rem;
  }

  ::v-deep .bankruptcy-table thead th {
    padding: 10px 6px;
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .search-primary {
    max-width: 200px;
  }

  .year-selector {
    min-width: 100px;
  }

  ::v-deep .bankruptcy-table {
    font-size: 0.75rem;
  }

  ::v-deep .bankruptcy-table tbody td {
    padding: 6px 4px;
  }

  ::v-deep .bankruptcy-table thead th {
    padding: 8px 4px;
    font-size: 0.75rem;
  }

  ::v-deep .bankruptcy-table .badge {
    font-size: 0.7rem;
    padding: 2px 6px;
  }
}

/* 修復表格溢出 */
::v-deep .table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  overflow-x: auto;
}

::v-deep .table-responsive .table {
  margin-bottom: 0;
}
</style>